import React, { useEffect, useState, useCallback, useRef } from "react";
import { Loader2, RefreshCw, Lightbulb, LightbulbOff, Pencil, Save, X, Plus, Wifi, WifiOff } from "lucide-react";
import axios from "axios";
import { toast } from "../../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: `${BACKEND_URL}/api` });
client.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("admin_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Livolo lights admin panel: one section per configured room. Shows every
// aliased switch channel with its live on/off state; the admin can rename
// the alias, toggle a light, or send an "all on" / "all off" group action.
export default function LivoloLightsPanel() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingAccount, setAddingAccount] = useState(false);

  const loadAccounts = useCallback(async () => {
    try {
      const { data } = await client.get("/livolo/accounts");
      setAccounts(data.accounts || []);
    } catch (e) {
      toast({ title: "Errore Livolo", description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  if (loading) return (
    <div className="rounded-2xl bg-white border border-[#e8dcc8] p-8 flex justify-center">
      <Loader2 className="h-5 w-5 animate-spin text-[#4a1c17]" />
    </div>
  );

  return (
    <div className="rounded-2xl bg-white border border-[#e8dcc8] p-4 space-y-3" data-testid="livolo-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-serif text-lg font-bold text-[#2b1410]">Luci Livolo · Controllo diretto</h3>
        <button
          onClick={() => setAddingAccount(true)}
          data-testid="livolo-add-account"
          className="inline-flex items-center gap-1 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-1.5 text-xs font-semibold"
        >
          <Plus className="h-3 w-3" /> Aggiungi camera
        </button>
      </div>

      {accounts.length === 0 && !addingAccount && (
        <div className="rounded-xl border border-dashed border-[#e8dcc8] p-6 text-center text-sm text-[#7c5a4e]">
          Nessuna camera configurata ancora. Aggiungi un account Livolo per iniziare.
        </div>
      )}

      {addingAccount && <AddAccountForm onCancel={() => setAddingAccount(false)} onSaved={() => { setAddingAccount(false); loadAccounts(); }} />}

      {accounts.map((a) => (
        <RoomSection key={a.room} account={a} onChanged={loadAccounts} />
      ))}
    </div>
  );
}

function AddAccountForm({ onCancel, onSaved }) {
  const [room, setRoom] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!room || !email || !password) { toast({ title: "Compila tutti i campi" }); return; }
    setBusy(true);
    try {
      await client.post("/livolo/accounts", { room: room.toLowerCase(), email, password, country_code: "IT" });
      toast({ title: "Account Livolo salvato e loggato" });
      onSaved();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-[#e8dcc8] bg-[#faf3ee] p-3 space-y-2">
      <div className="grid sm:grid-cols-3 gap-2">
        <input value={room} onChange={(e) => setRoom(e.target.value)} placeholder="Camera (es. argo)"
          className="rounded-md border border-[#e8dcc8] bg-white px-2 py-1.5 text-sm" data-testid="livolo-form-room" />
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email Livolo Home"
          className="rounded-md border border-[#e8dcc8] bg-white px-2 py-1.5 text-sm" data-testid="livolo-form-email" />
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
          className="rounded-md border border-[#e8dcc8] bg-white px-2 py-1.5 text-sm" data-testid="livolo-form-password" />
      </div>
      <div className="flex gap-2">
        <button onClick={save} disabled={busy}
          data-testid="livolo-form-save"
          className="inline-flex items-center gap-1 rounded-full bg-[#4a1c17] text-[#f7ebdf] px-3 py-1.5 text-xs font-semibold hover:bg-[#2b1410] disabled:opacity-40">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Salva e login
        </button>
        <button onClick={onCancel} className="inline-flex items-center gap-1 rounded-full border border-[#e8dcc8] px-3 py-1.5 text-xs">
          <X className="h-3 w-3" /> Annulla
        </button>
      </div>
    </div>
  );
}

function RoomSection({ account, onChanged }) {
  const [lights, setLights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState({});
  const [editingKey, setEditingKey] = useState(null);
  const inFlight = useRef(false);
  const firstLoad = useRef(true);

  // Silent refresh: keeps the grid on screen and merges the new snapshot
  // into the previous one so a transient offline read (state=null) never
  // blanks a card that was known a moment ago.
  const load = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setRefreshing(true);
    try {
      const { data } = await client.get(`/livolo/rooms/${account.room}/lights`);
      const fresh = data.lights || [];
      setLights((prev) => {
        const prevBy = Object.fromEntries(prev.map((p) => [`${p.iot_id}-${p.channel}`, p]));
        return fresh.map((l) => {
          const old = prevBy[`${l.iot_id}-${l.channel}`];
          if (l.state === null && old && old.state !== null) return { ...l, state: old.state, stale: true };
          return { ...l, stale: false };
        });
      });
    } catch (e) {
      if (firstLoad.current) toast({ title: `Errore su ${account.room}`, description: e?.response?.data?.detail || e.message });
    } finally {
      inFlight.current = false;
      firstLoad.current = false;
      setLoading(false);
      setRefreshing(false);
    }
  }, [account.room]);

  useEffect(() => {
    load();
    const t = setInterval(() => {
      // Pause while renaming an alias, while a toggle is in flight, or
      // when the tab is in background.
      if (editingKey || document.hidden || Object.values(busy).some(Boolean)) return;
      load();
    }, 20000);
    return () => clearInterval(t);
  }, [load, editingKey, busy]);

  const toggle = async (l) => {
    const key = `${l.iot_id}-${l.channel}`;
    setBusy((b) => ({ ...b, [key]: true }));
    const action = l.state === "on" ? "off" : "on";
    try {
      await client.post(`/livolo/rooms/${account.room}/lights/${l.iot_id}/${l.channel}/${action}`);
      setLights((prev) => prev.map((p) => (`${p.iot_id}-${p.channel}` === key ? { ...p, state: action, stale: false } : p)));
      setTimeout(load, 1500);
    } catch (e) {
      toast({ title: "Comando fallito", description: e?.response?.data?.detail || e.message });
    } finally { setBusy((b) => ({ ...b, [key]: false })); }
  };

  const allAction = async (action) => {
    try {
      await client.post(`/livolo/rooms/${account.room}/lights/all/${action}`);
      setLights((prev) => prev.map((p) => (p.state === null ? p : { ...p, state: action, stale: false })));
      setTimeout(load, 1500);
      toast({ title: `Tutte ${action === "on" ? "accese" : "spente"}` });
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    }
  };

  const deleteAccount = async () => {
    if (!confirm(`Rimuovere l'account Livolo di ${account.room}?`)) return;
    try {
      await client.delete(`/livolo/accounts/${account.room}`);
      onChanged();
    } catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail || e.message }); }
  };

  return (
    <div className="rounded-xl border border-[#e8dcc8] p-3 space-y-2" data-testid={`livolo-room-${account.room}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <span className="font-serif text-base font-bold text-[#2b1410] uppercase">{account.room}</span>
          {account.identity_id
            ? <span className="ml-2 text-[10px] text-emerald-700 inline-flex items-center gap-0.5"><Wifi className="h-3 w-3" /> connesso</span>
            : <span className="ml-2 text-[10px] text-amber-700 inline-flex items-center gap-0.5"><WifiOff className="h-3 w-3" /> non connesso</span>}
          <span className="ml-2 text-[11px] text-[#7c5a4e]">{account.email}</span>
        </div>
        <div className="flex gap-1">
          <button onClick={() => allAction("on")} data-testid={`livolo-all-on-${account.room}`}
            className="text-[11px] rounded-full bg-amber-500 hover:bg-amber-600 text-white px-2.5 py-1 font-semibold">Tutte ON</button>
          <button onClick={() => allAction("off")} data-testid={`livolo-all-off-${account.room}`}
            className="text-[11px] rounded-full bg-neutral-700 hover:bg-neutral-800 text-white px-2.5 py-1 font-semibold">Tutte OFF</button>
          <button onClick={load} disabled={refreshing} title="Aggiorna stato" className="text-[11px] text-[#7c5a4e] hover:text-[#2b1410] p-1 disabled:opacity-60" data-testid={`livolo-refresh-${account.room}`}>
            <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} />
          </button>
          <button onClick={deleteAccount} className="text-[11px] text-red-600 hover:text-red-700 p-1" data-testid={`livolo-del-${account.room}`}>
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>

      {loading && lights.length === 0 ? (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
          {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-[58px] rounded-lg border border-[#e8dcc8] bg-neutral-50 animate-pulse" />)}
        </div>
      ) : lights.length === 0 ? (
        <div className="text-xs text-[#7c5a4e]">Nessun alias configurato. Vai alla pagina API o attendi il seed.</div>
      ) : (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
          {lights.map((l) => {
            const key = `${l.iot_id}-${l.channel}`;
            const on = l.state === "on";
            const isBusy = !!busy[key];
            return (
              <div key={key} className={`rounded-lg border p-2 ${on ? "bg-amber-50 border-amber-300" : "bg-neutral-50 border-[#e8dcc8]"}`} data-testid={`light-${l.iot_id}-${l.channel}`}>
                {editingKey === key
                  ? <NameEditor light={l} room={account.room} onDone={() => { setEditingKey(null); load(); }} />
                  : (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggle(l)}
                        disabled={isBusy || l.state === null}
                        className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${on ? "bg-amber-400 text-white" : "bg-neutral-300 text-neutral-700"} disabled:opacity-40`}
                        data-testid={`light-toggle-${l.iot_id}-${l.channel}`}
                      >
                        {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : on ? <Lightbulb className="h-5 w-5" /> : <LightbulbOff className="h-5 w-5" />}
                      </button>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-[#2b1410] truncate">{l.name}</div>
                        <div className="text-[10px] text-[#7c5a4e]">
                          {l.state === null ? "offline" : (on ? "accesa" : "spenta")}
                          {l.stale && <span className="ml-1 text-amber-700">· non aggiornato</span>}
                        </div>
                      </div>
                      <button onClick={() => setEditingKey(key)} className="text-[#7c5a4e] hover:text-[#2b1410] p-1">
                        <Pencil className="h-3 w-3" />
                      </button>
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NameEditor({ light, room, onDone }) {
  const [name, setName] = useState(light.name);
  const [group, setGroup] = useState(light.group || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await client.post("/livolo/lights", {
        room, iot_id: light.iot_id, channel: light.channel, name, group,
      });
      onDone();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-1">
      <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded border border-[#e8dcc8] bg-white px-2 py-1 text-xs"
        placeholder="Nome (es. Bagno · spot)" />
      <div className="flex gap-1">
        <select value={group} onChange={(e) => setGroup(e.target.value)} className="flex-1 rounded border border-[#e8dcc8] bg-white px-2 py-1 text-xs">
          <option value="">— nessun gruppo —</option>
          <option value="bagno">bagno</option>
          <option value="camera">camera</option>
        </select>
        <button onClick={save} disabled={busy} className="rounded bg-[#4a1c17] text-white px-2 py-1 text-xs disabled:opacity-40">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
        </button>
        <button onClick={onDone} className="rounded border border-[#e8dcc8] px-2 py-1 text-xs"><X className="h-3 w-3" /></button>
      </div>
    </div>
  );
}
